import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import studyResourceEnquiryIssueService from '../../../services/StudyResourceEnquiryIssueService';
import ApiService from '../../../_services/ApiService';
import Config from '../../../_config/config';
import myAxios from '../../../_services/axiositeachWrapper';

interface Props {
  show: boolean;
  onHide: () => void;
  resourceId: number | null;
  resourceName: string;
}

type SchoolOption = { schoolId: number; schoolName: string };
type YearOption = { yearId: number; yearName: string };
type ProgramOption = { programId: number; programName: string };
type CourseOption = { courseId: number; courseName: string };
type DeliveryFormatOption = { Id: number; Name: string };

const StudyResourceEnquiryIssueModal: React.FC<Props> = ({
  show,
  onHide,
  resourceId,
  resourceName,
}) => {
  // Dropdown data
  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [years, setYears] = useState<YearOption[]>([]);
  const [programs, setPrograms] = useState<ProgramOption[]>([]);
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [deliveryFormats, setDeliveryFormats] = useState<DeliveryFormatOption[]>([]);

  // Selected values
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | ''>('');
  const [selectedYearId, setSelectedYearId] = useState<number | ''>('');
  const [selectedProgramId, setSelectedProgramId] = useState<number | ''>('');
  const [selectedCourseId, setSelectedCourseId] = useState<number | ''>('');
  const [selectedDeliveryFormatId, setSelectedDeliveryFormatId] = useState<number | ''>('');

  // Dates - default: today → 1 year from today
  const [visibleFrom, setVisibleFrom] = useState(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  });
  const [visibleTo, setVisibleTo] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  });

  // System config
  const [isProgramBased, setIsProgramBased] = useState<boolean | null>(null);

  // Issue history
  const [issueHistory, setIssueHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // UI states
  const [loading, setLoading] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [loadingYears, setLoadingYears] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingDeliveryFormats, setLoadingDeliveryFormats] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Load history function
  const loadHistory = async () => {
    if (!resourceId) return;

    setLoadingHistory(true);
    try {
      const history = await studyResourceEnquiryIssueService.getEnquiryLevelIssues(resourceId);
      setIssueHistory(Array.isArray(history) ? history : []);
    } catch (err) {
      setIssueHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load schools, years, delivery formats, system config, and history on mount
  useEffect(() => {
    if (!show || !resourceId) return;

    ApiService.getScheduleSystemConfig()
      .then((res) => {
        if (res.data?.success) setIsProgramBased(res.data.data.isProgramBased);
      })
      .catch(() => setIsProgramBased(false));

    setLoadingSchools(true);
    ApiService.getAcademicSchools()
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setSchools(Array.isArray(data) ? data : []);
      })
      .catch(() => setSchools([]))
      .finally(() => setLoadingSchools(false));

    setLoadingYears(true);
    ApiService.getAcademicYears()
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setYears(Array.isArray(data) ? data : []);
      })
      .catch(() => setYears([]))
      .finally(() => setLoadingYears(false));

    loadHistory();
  }, [show, resourceId]);

  // When year changes → fetch programs, reset downstream
  useEffect(() => {
    setPrograms([]);
    setCourses([]);
    setSelectedProgramId('');
    setSelectedCourseId('');

    if (!selectedYearId) return;

    setLoadingPrograms(true);
    ApiService.getAcademicPrograms(String(selectedYearId))
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setPrograms(Array.isArray(data) ? data : []);
      })
      .catch(() => setPrograms([]))
      .finally(() => setLoadingPrograms(false));
  }, [selectedYearId]);

  // When program changes → fetch courses filtered by year+program
  useEffect(() => {
    setCourses([]);
    setSelectedCourseId('');

    if (!selectedYearId) return;

    setLoadingCourses(true);
    ApiService.getAcademicCourses(
      Number(selectedYearId),
      selectedProgramId ? Number(selectedProgramId) : undefined
    )
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setCourses(Array.isArray(data) ? data : []);
      })
      .catch(() => setCourses([]))
      .finally(() => setLoadingCourses(false));
  }, [selectedYearId, selectedProgramId]);

  // When course changes → fetch delivery formats filtered by course
  useEffect(() => {
    setDeliveryFormats([]);
    setSelectedDeliveryFormatId('');

    if (!selectedCourseId) return;

    setLoadingDeliveryFormats(true);
    myAxios
      .get(`${Config.nodeSiteUrl}v1/program-details/delivery-formats?courseId=${selectedCourseId}`)
      .then((res) => {
        const data = res.data || [];
        setDeliveryFormats(Array.isArray(data) ? data : []);
      })
      .catch(() => setDeliveryFormats([]))
      .finally(() => setLoadingDeliveryFormats(false));
  }, [selectedCourseId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!resourceId) return;
    if (!selectedSchoolId) {
      setMessage({ type: 'error', text: 'Please select a School' });
      return;
    }
    if (!visibleTo) {
      setMessage({ type: 'error', text: 'Please select Visible To date' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      await studyResourceEnquiryIssueService.createEnquiryLevelIssue({
        resourceId,
        type: 'school',
        schoolId: selectedSchoolId ? Number(selectedSchoolId) : undefined,
        yearId: selectedYearId ? Number(selectedYearId) : undefined,
        programId: selectedProgramId ? Number(selectedProgramId) : undefined,
        courseId: selectedCourseId ? Number(selectedCourseId) : undefined,
        deliveryFormatId: selectedDeliveryFormatId ? Number(selectedDeliveryFormatId) : undefined,
        startDate: visibleFrom,
        endDate: visibleTo,
      });

      setLoading(false);
      setMessage({ type: 'success', text: 'Resource issued successfully at enquiry level' });
      resetForm(false); // Don't clear the success message
      loadHistory(); // Reload history after successful issue
    } catch (err: any) {
      setLoading(false);
      setMessage({ type: 'error', text: err.message || 'Failed to issue resource' });
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;

    setDeleting(true);
    try {
      await studyResourceEnquiryIssueService.deleteEnquiryLevelIssue(deleteConfirmId);
      setMessage({ type: 'success', text: 'Issue deleted successfully' });
      setDeleteConfirmId(null);
      loadHistory(); // Reload history after deletion
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to delete issue' });
    } finally {
      setDeleting(false);
    }
  };

  const resetForm = (clearMessage = true) => {
    setSelectedSchoolId('');
    setSelectedYearId('');
    setSelectedProgramId('');
    setSelectedCourseId('');
    setSelectedDeliveryFormatId('');
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    setVisibleFrom(now.toISOString().slice(0, 16));
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    setVisibleTo(d.toISOString().slice(0, 16));
    if (clearMessage) {
      setMessage(null);
    }
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (!resourceId) return null;

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          Enquiry Level Issue - {resourceName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Issue Form Card */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <span>Issue</span>
            </div>
            <div style={{ padding: 20 }}>
              <form onSubmit={handleSubmit}>
                {/* Cascading Dropdowns — 2-column grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                  {/* School */}
                  <div>
                    <label style={labelStyle}>
                      School <span style={{ color: 'var(--status-danger)' }}>*</span>
                    </label>
                    <select
                      value={selectedSchoolId}
                      onChange={(e) => setSelectedSchoolId(e.target.value ? Number(e.target.value) : '')}
                      style={selectStyle}
                      required
                    >
                      <option value="">-- Select School --</option>
                      {schools.map((s) => (
                        <option key={s.schoolId} value={s.schoolId}>
                          {s.schoolName}
                        </option>
                      ))}
                    </select>
                    {loadingSchools && <span style={loadingHint}>Loading...</span>}
                  </div>

                  {/* Year */}
                  <div>
                    <label style={labelStyle}>Year</label>
                    <select
                      value={selectedYearId}
                      onChange={(e) => setSelectedYearId(e.target.value ? Number(e.target.value) : '')}
                      style={selectStyle}
                    >
                      <option value="">-- Select Year --</option>
                      {years.map((y) => (
                        <option key={y.yearId} value={y.yearId}>
                          {y.yearName}
                        </option>
                      ))}
                    </select>
                    {loadingYears && <span style={loadingHint}>Loading...</span>}
                  </div>

                  {/* Program — shown for program-based systems */}
                  {isProgramBased !== false && (
                    <div>
                      <label style={labelStyle}>Program</label>
                      <select
                        value={selectedProgramId}
                        onChange={(e) => setSelectedProgramId(e.target.value ? Number(e.target.value) : '')}
                        style={selectStyle}
                        disabled={!selectedYearId}
                      >
                        <option value="">{selectedYearId ? '-- Select Program --' : 'Select Year first'}</option>
                        {programs.map((p) => (
                          <option key={p.programId} value={p.programId}>
                            {p.programName}
                          </option>
                        ))}
                      </select>
                      {loadingPrograms && <span style={loadingHint}>Loading...</span>}
                    </div>
                  )}

                  {/* Course */}
                  <div>
                    <label style={labelStyle}>Course</label>
                    {loadingCourses ? (
                      <div style={{ padding: 8, color: 'var(--text-muted)', fontSize: 13 }}>Loading...</div>
                    ) : !selectedYearId || (isProgramBased === true && !selectedProgramId) ? (
                      <div style={{ padding: '8px 10px', fontSize: 13, color: 'var(--text-faint)', border: '1px solid var(--border-strong)', borderRadius: 6, background: 'var(--surface-page)' }}>
                        {!selectedYearId ? 'Select Year first' : 'Select Program first'}
                      </div>
                    ) : courses.length === 0 ? (
                      <div style={{ padding: '8px 10px', fontSize: 13, color: 'var(--text-faint)', border: '1px solid var(--border-strong)', borderRadius: 6, background: 'var(--surface-page)' }}>
                        No courses found
                      </div>
                    ) : (
                      <select
                        value={selectedCourseId}
                        onChange={(e) => setSelectedCourseId(e.target.value ? Number(e.target.value) : '')}
                        style={selectStyle}
                      >
                        <option value="">-- Select Course --</option>
                        {courses.map((c) => (
                          <option key={c.courseId} value={c.courseId}>
                            {c.courseName}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Delivery Format */}
                  <div>
                    <label style={labelStyle}>Delivery Format</label>
                    {loadingDeliveryFormats ? (
                      <div style={{ padding: 8, color: 'var(--text-muted)', fontSize: 13 }}>Loading...</div>
                    ) : !selectedCourseId ? (
                      <div style={{ padding: '8px 10px', fontSize: 13, color: 'var(--text-faint)', border: '1px solid var(--border-strong)', borderRadius: 6, background: 'var(--surface-page)' }}>
                        Select Course first
                      </div>
                    ) : deliveryFormats.length === 0 ? (
                      <div style={{ padding: '8px 10px', fontSize: 13, color: 'var(--text-faint)', border: '1px solid var(--border-strong)', borderRadius: 6, background: 'var(--surface-page)' }}>
                        No delivery formats found
                      </div>
                    ) : (
                      <select
                        value={selectedDeliveryFormatId}
                        onChange={(e) => setSelectedDeliveryFormatId(e.target.value ? Number(e.target.value) : '')}
                        style={selectStyle}
                      >
                        <option value="">-- Select Delivery Format --</option>
                        {deliveryFormats.map((df) => (
                          <option key={df.Id} value={df.Id}>
                            {df.Name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Visible From */}
                  <div>
                    <label style={labelStyle}>Visible From</label>
                    <input
                      type="datetime-local"
                      value={visibleFrom}
                      onChange={(e) => setVisibleFrom(e.target.value)}
                      style={inputStyle}
                    />
                  </div>

                  {/* Visible To */}
                  <div>
                    <label style={labelStyle}>
                      Visible To <span style={{ color: 'var(--status-danger)' }}>*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={visibleTo}
                      onChange={(e) => setVisibleTo(e.target.value)}
                      style={inputStyle}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                  <Button variant="secondary" onClick={handleClose} disabled={loading}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={loading}>
                    {loading ? 'Issuing...' : 'Issue'}
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {/* Success/Error Message - displayed above history */}
          {message && (
            <div
              style={{
                padding: '12px 16px',
                borderRadius: 6,
                backgroundColor: message.type === 'success' ? '#ecfdf5' : '#fef2f2',
                color: message.type === 'success' ? '#065f46' : '#991b1b',
                border: `1px solid ${message.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
                fontSize: 14,
                fontWeight: 500,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>{message.text}</span>
              <button
                onClick={() => setMessage(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 18,
                  fontWeight: 'bold',
                  color: 'inherit',
                  padding: '0 4px',
                }}
              >
                ×
              </button>
            </div>
          )}

          {/* Issue History Card */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <span>Issue History</span>
              <button
                onClick={loadHistory}
                disabled={loadingHistory}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--brand-primary)',
                  cursor: loadingHistory ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  textDecoration: 'underline',
                }}
              >
                {loadingHistory ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            <div style={{ padding: 20 }}>
              {loadingHistory && issueHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 14 }}>
                  Loading issue history...
                </div>
              ) : issueHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 14 }}>
                  No issues found. Issue this resource to create history.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={tableStyle}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border-default)', textAlign: 'left' }}>
                        <th style={thStyle}>School</th>
                        <th style={thStyle}>Course</th>
                        <th style={thStyle}>Year</th>
                        {isProgramBased !== false && <th style={thStyle}>Program</th>}
                        <th style={thStyle}>Delivery Format</th>
                        <th style={thStyle}>Visible From</th>
                        <th style={thStyle}>Visible To</th>
                        <th style={thStyle}>Issued On</th>
                        <th style={thStyle}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {issueHistory.map((issue: any) => (
                        <tr key={issue.Id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                          <td style={tdStyle}>{issue.schoolName || '-'}</td>
                          <td style={tdStyle}>{issue.courseName || '-'}</td>
                          <td style={tdStyle}>{issue.yearName || '-'}</td>
                          {isProgramBased !== false && <td style={tdStyle}>{issue.programName || '-'}</td>}
                          <td style={tdStyle}>{issue.deliveryFormatName || '-'}</td>
                          <td style={tdStyle}>{formatDate(issue.StartDate)}</td>
                          <td style={tdStyle}>{formatDate(issue.EndDate)}</td>
                          <td style={tdStyle}>{formatDate(issue.CreationDate)}</td>
                          <td style={tdStyle}>
                            <button
                              onClick={() => setDeleteConfirmId(issue.Id)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--status-danger)',
                                cursor: 'pointer',
                                fontSize: 13,
                                textDecoration: 'underline',
                              }}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>

      {/* Delete Confirmation Modal */}
      <Modal show={deleteConfirmId !== null} onHide={() => setDeleteConfirmId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to remove this issue? The resource will no longer be visible to students.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteConfirmId(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

// ── Shared inline styles ───────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  background: 'var(--surface-card)',
  border: '1px solid var(--border-default)',
  borderRadius: 16,
  boxShadow: '0 1px 2px rgba(0,0,0,.04)',
  overflow: 'hidden',
  marginBottom: 16,
};

const cardHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '14px 18px',
  borderBottom: '1px solid var(--border-default)',
  fontWeight: 600,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 4,
  fontWeight: 600,
  fontSize: 13,
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid var(--border-strong)',
  fontSize: 13,
  backgroundColor: 'var(--surface-card)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid var(--border-strong)',
  fontSize: 13,
};

const loadingHint: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--text-faint)',
  marginTop: 2,
  display: 'inline-block',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const thStyle: React.CSSProperties = {
  padding: '12px 8px',
  fontWeight: 600,
  color: 'var(--text-body)',
  textAlign: 'left',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 8px',
  color: 'var(--text-muted)',
};

export default StudyResourceEnquiryIssueModal;
